import React, { useMemo, useCallback } from 'react';
import DateRangePicker from '@/shared/components/forms/DateRangePicker';
import SearchableSelect, { SelectOption } from '@/shared/components/forms/SearchableSelect';
import { DeliveryHistoryDetailFilters, DeliverySupplier, DeliveryRider } from '../types/deliveryHistoryDetail.types';

interface DetailFilterSectionProps {
    filters: DeliveryHistoryDetailFilters;
    isSupplierUser: boolean;
    suppliers: DeliverySupplier[];
    suppliersLoading: boolean;
    riders: DeliveryRider[];
    ridersLoading: boolean;
    loading: boolean;
    excelDownloading: boolean;
    onFilterChange: <K extends keyof DeliveryHistoryDetailFilters>(key: K, value: DeliveryHistoryDetailFilters[K]) => void;
    onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
    onExcelDownload: () => void;
}

const DetailFilterSection: React.FC<DetailFilterSectionProps> = ({
                                                                     filters,
                                                                     isSupplierUser,
                                                                     suppliers,
                                                                     suppliersLoading,
                                                                     riders,
                                                                     ridersLoading,
                                                                     loading,
                                                                     excelDownloading,
                                                                     onFilterChange,
                                                                     onSubmit,
                                                                     onExcelDownload,
                                                                 }) => {
    const supplierSelectOptions: SelectOption[] = useMemo(() => {
        const options = [...suppliers];
        if (filters.suppId && !options.some((s) => s.suppId === filters.suppId)) {
            options.unshift({ suppId: filters.suppId, suppName: filters.suppName || filters.suppId });
        }
        return options.map((s) => ({ value: s.suppId, label: s.suppName }));
    }, [suppliers, filters.suppId, filters.suppName]);

    const handleSupplierChange = useCallback(
        (selectedId: string) => {
            const selectedSupplier = suppliers.find((s) => s.suppId === selectedId);
            onFilterChange('suppId', selectedId);
            onFilterChange('suppName', selectedSupplier?.suppName ?? '');
            onFilterChange('riderId', '');
            onFilterChange('riderName', '');
        },
        [suppliers, onFilterChange]
    );

    const handleRiderChange = useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>) => {
            const selectedId = e.target.value;
            const selectedRider = riders.find((r) => r.riderId === selectedId);
            onFilterChange('riderId', selectedId);
            onFilterChange('riderName', selectedRider?.riderName ?? '');
        },
        [riders, onFilterChange]
    );

    return (
        <div className="card search-card">
            <div className="card-body">
                <form className={`search-form ${isSupplierUser ? 'supplier-view' : ''}`} onSubmit={onSubmit}>
                    <div className="form-group date-range-group">
                        <div className="form-field">
                            <label className="form-label">
                                <i className="fas fa-calendar-alt" />
                                조회 기간
                            </label>
                            <DateRangePicker
                                startDate={filters.startDate}
                                endDate={filters.endDate}
                                onStartDateChange={(value) => onFilterChange('startDate', value)}
                                onEndDateChange={(value) => onFilterChange('endDate', value)}
                                startLabel="시작일"
                                endLabel="종료일"
                            />
                        </div>
                    </div>

                    {!isSupplierUser && (
                        <div className="form-group">
                            <div className="form-field">
                                <label className="form-label">
                                    <i className="fas fa-building" />
                                    협력사
                                </label>
                                <SearchableSelect
                                    options={supplierSelectOptions}
                                    value={filters.suppId}
                                    onChange={handleSupplierChange}
                                    placeholder="협력사 검색..."
                                    disabled={suppliersLoading}
                                    showAllOption={false}
                                />
                            </div>
                        </div>
                    )}

                    <div className="form-group">
                        <div className="form-field">
                            <label className="form-label">
                                <i className="fas fa-motorcycle" />
                                라이더
                            </label>
                            <div className="rider-select-wrapper">
                                <select
                                    className="rider-select"
                                    value={filters.riderId}
                                    onChange={handleRiderChange}
                                    disabled={!filters.suppId || ridersLoading || riders.length === 0}
                                >
                                    <option value="">전체</option>
                                    {riders.map((r) => (
                                        <option key={r.riderId} value={r.riderId}>
                                            {r.riderName}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="form-group button-group">
                        <button
                            type="button"
                            className="btn btn-success excel-btn"
                            onClick={onExcelDownload}
                            disabled={loading || excelDownloading || !filters.startDate || !filters.endDate}
                        >
                            {excelDownloading ? (
                                <>
                                    <i className="fas fa-spinner fa-spin"></i>
                                    다운로드 중...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-file-excel"></i>
                                    엑셀
                                </>
                            )}
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={loading || !filters.suppId}>
                            <i className="fas fa-search" />
                            검색
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default DetailFilterSection;
